import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import { format, parse } from 'date-fns';

import Calendar from 'react-calendar';

import useToggleState from '../../hooks/useToggleState';

import InputTextLine from '../InputTextLine';
import IconButton from '../IconButton';
import Popover from '../Popover';

import { InputWrapper, IconWrapper, CalendarWrapper } from './styled/DatePicker.styled';
import { Close, Date as DateIcon } from '../../icons';

const datePickerDefaultPropOnChange = () => {};

const DatePicker = ({
  value = new Date(),
  label = '',
  startDate = null,
  endDate = null,
  minDate = null,
  maxDate = null,
  onChange = datePickerDefaultPropOnChange,
  dateFormat = 'yyyy-MM-dd',
  readOnly = true,
  isClearable = false,
  stayOpen = true,
  atModal = false,
}) => {
  const focusBlock = useRef();
  const theme = useTheme();
  const [popState,, closePop, openPop] = useToggleState(false);
  const [strDate, setStrDate] = useState({ value: '', error: true, errorMsg: '' });

  const handleTextInput = (e) => {
    const val = e.target.value;
    const fVal = val.replace(/\D/g, '').split('');
    let aux = '';
    let j = 0;
    for (let i = 0; i < fVal.length; i += 1) {
      if (['y', 'M', 'd'].includes(dateFormat[j])) {
        aux += fVal[i];
        j += 1;
      } else if (dateFormat[j]) {
        aux = aux + dateFormat[j] + fVal[i];
        j += 2;
      } else break;
    }
    const dt = parse(aux, dateFormat, new Date());
    if (Number.isNaN(dt.valueOf())) {
      setStrDate({
        value: aux,
        error: true,
        errorMsg: 'Informe uma data válida',
      });
    } else {
      setStrDate({
        value: aux,
        error: false,
        errorMsg: '',
      });
      onChange(dt);
    }
  };

  const handleCalendarPick = (date) => {
    setStrDate({
      value: date ? format(date, dateFormat) : '',
      error: false,
      errorMsg: '',
    });
    onChange(date);
  };

  const handleInputBlur = () => {
    focusBlock.current = false;
    if (strDate.error) {
      handleCalendarPick(null);
    } else {
      handleCalendarPick(value);
    }
  };

  const handleInputFocus = () => {
    focusBlock.current = true;
  };

  const clearInput = () => {
    handleCalendarPick(null);
  };

  useEffect(() => {
    if (focusBlock.current) return;
    setStrDate({
      value: value ? format(value, dateFormat) : '',
      error: false,
      errorMsg: '',
    });
  }, [value]);

  return (
    <Popover
      style={{
        borderRadius: '8px',
        backgroundColor: theme.popoverBackground,
        border: '1px solid',
        borderColor: theme.grey,
        zIndex: atModal ? '1050' : '100',
        overflow: 'hidden',
      }}
      containerStyle={{ width: '100%' }}
      offset={[0, 10]}
      open={popState}
      closePopover={closePop}
      stayOpened
    >
      <Popover.Action>
        <InputWrapper onClick={openPop}>
          <InputTextLine
            value={strDate.value}
            error={strDate.errorMsg.length > 0}
            errorMessage={strDate.errorMsg}
            label={label}
            onChange={handleTextInput}
            readOnly={readOnly}
            fullWidth
            style={{
              paddingRight: isClearable && value ? '55px' : '35px',
              cursor: 'pointer',
            }}
            wrapperStyle={{ width: '100%' }}
            onBlur={handleInputBlur}
            onFocus={handleInputFocus}
          />
          {isClearable && value && (
            <IconButton
              size="small"
              color="grey"
              style={{
                position: 'absolute',
                bottom: '7px',
                right: '32px',
                boxShadow: 'none',
                padding: '0',
                aspectRatio: '1',
              }}
              onClick={clearInput}
            >
              <Close />
            </IconButton>
          )}
          <IconWrapper>
            <DateIcon />
          </IconWrapper>
        </InputWrapper>
      </Popover.Action>
      <Popover.Content>
        <CalendarWrapper>
          <Calendar
            value={value}
            onChange={handleCalendarPick}
            minDate={minDate}
            maxDate={maxDate}
            minDetail="decade"
            tileClassName={({ date }) => (
              (startDate && startDate <= date) && (endDate && endDate >= date) ? (
                'react-calendar__tile--selected'
              ) : null
            )}
            onClickDay={() => stayOpen || closePop()}
          />
        </CalendarWrapper>
      </Popover.Content>
    </Popover>
  );
};

DatePicker.propTypes = {
  value: PropTypes.instanceOf(Date),
  label: PropTypes.string,
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  onChange: PropTypes.func,
  isClearable: PropTypes.bool,
  dateFormat: PropTypes.string,
  readOnly: PropTypes.bool,
  atModal: PropTypes.bool,
  stayOpen: PropTypes.bool,
};

export default DatePicker;
